import PageWithTitle from '@growdash/growintel/components/hocs/with-title';
import {
  AvgPriceByMonth,
  CuisineOperationBenchmark,
  PaginatedResponsePopularMenuItem,
} from '@growdash/growintel/generated';
import useBrands from '@growdash/growintel/hooks/useBrands';
import { useGrowintelService } from '@growdash/growintel/hooks/useGrowintelService';
import useTabState from '@growdash/growintel/hooks/useTabState';

import SectionTitle from '@growdash/growintel/components/atoms/section-title';

import {
  Card,
  CardContent,
  CardHeader,
  Typography,
} from '@growdash/design-system/atoms';
import PageFooter from '@growdash/growintel/components/molecules/page-footer';

import Target from '@growdash/growintel/assets/icons/target';
import { useGrowintel } from '@growdash/growintel/hooks/useGrowIntel';
import useGrowintUser from '@growdash/growintel/hooks/useGrowintUser';
import { executeFacadeRequest } from '@growdash/growintel/lib/aisha';
import CuisineBenchmarkView from '@growdash/growintel/pages/trends/views/cuisine-benchmark-view';
import NewClosedResturantsView from '@growdash/growintel/pages/trends/views/new-closed-restaurants-view';
import PopularItemsView from '@growdash/growintel/pages/trends/views/popular-items-view';
import { useQuery } from '@tanstack/react-query';
import { ChartLine } from 'lucide-react';
import { useMemo } from 'react';
import TrendsOnboardingDialog from './components/onboarding-dialog';
import { NewClosedRestaurants } from './types';
import EmptyStateView from './views/_empty-state-view';
import LoadingView from './views/_loading-view';
import AishaInsightsView from './views/aisha-insights-view';
import AvgPricesView from './views/avg-prices-view';
import OverviewCardsView from './views/overview-cards-view';
import TrendsFiltersView from './views/trends-filters-view';

export default function TrendsView() {
  // ---- Setup & State
  const userBrands = useBrands();
  const user = useGrowintUser();
  const service = useGrowintelService();
  const { lib } = useGrowintel();
  const { aishaService } = lib;

  const { filters } = useTabState('trends');
  const { city, cuisine, areas, brand } = filters;

  // Formatted filter parameters
  const brandIdFilter = useMemo(() => brand?.id, [brand]);
  const cuisineFilter = useMemo(() => cuisine || undefined, [cuisine]);
  const cityFilter = useMemo(
    () => (city && city !== 'ALL' ? city : undefined),
    [city],
  );

  const areasFilter = useMemo(
    () => (!!areas?.length ? areas : undefined),
    [areas],
  );

  const isEmptyState = !cuisine || !brand;

  // ---- Data Fetching
  const { data: popularItems, isFetching: popularItemsFetching } = useQuery({
    queryKey: ['trends-popular-items', cityFilter, cuisineFilter, areasFilter],
    queryFn: async (): Promise<
      PaginatedResponsePopularMenuItem | undefined
    > => {
      try {
        const response =
          await service?.menuItemsAPI.getPopularMenuItemsMenuItemsPopularGet(
            1,
            10,
            cityFilter,
            areasFilter,
            cuisineFilter,
          );
        return response?.data;
      } catch (error) {
        console.error('error during fetching trends', error);
      }
    },
    refetchOnWindowFocus: false,
  });

  const { data: restaurants, isFetching: restaurantsLoading } = useQuery({
    queryKey: [
      'trends-restaurants',
      brandIdFilter,
      cityFilter,
      areasFilter,
      cuisineFilter,
    ],
    queryFn: async (): Promise<NewClosedRestaurants | undefined> => {
      try {
        if (!brandIdFilter) return;
        const newResponse =
          await service?.branchesAPI.getNewBranchesBranchesNewGet(
            1,
            Number.MAX_SAFE_INTEGER,
            cityFilter,
            areasFilter,
            cuisineFilter,
          );
        const closedResponse =
          await service?.branchesAPI.getClosedBranchesBranchesClosedGet(
            1,
            Number.MAX_SAFE_INTEGER,
            cuisineFilter,
          );

        return { new: newResponse?.data, closed: closedResponse?.data };
      } catch (error) {
        console.error('error during fetching trends', error);
      }
    },
    refetchOnWindowFocus: false,
  });

  const { data: avgPricesData } = useQuery({
    queryKey: [
      'trends-avg-prices',
      brandIdFilter,
      cuisineFilter,
      cityFilter,
      areasFilter,
    ],
    queryFn: async (): Promise<AvgPriceByMonth[] | undefined | null> => {
      try {
        if (!brandIdFilter || !cuisineFilter) return null;
        const response =
          await service?.brandsAPI.getAvgPriceByMonthBrandsAvgPriceByMonthGet(
            cityFilter,
            areasFilter,
            cuisineFilter,
          );
        return response?.data;
      } catch (error) {
        console.error('error during fetching trends', error);
      }
    },
    refetchOnWindowFocus: false,
  });

  const avgOfAverages = avgPricesData?.length
    ? avgPricesData.reduce((sum, curr) => sum + (curr.avg_price || 0), 0) /
      avgPricesData.length
    : 0;

  const { data: cuisineBenchmark, isFetching: cuisineBenchmarkLoading } =
    useQuery({
      queryKey: [
        'trends-benchmark',
        brandIdFilter,
        cuisineFilter,
        cityFilter,
        areasFilter,
      ],
      enabled: !!brandIdFilter && !!cuisineFilter,
      queryFn: async (): Promise<CuisineOperationBenchmark | undefined> => {
        try {
          if (!brandIdFilter || !cuisineFilter) return;
          const response =
            await service?.brandsAPI.getCuisineOperationBenchmarkBrandsCuisineOperationBenchmarkGet(
              brandIdFilter,
              cuisineFilter,
              cityFilter,
              areasFilter,
            );

          return response?.data;
        } catch (error) {
          console.error('error during fetching trends', error);
        }
      },
      refetchOnWindowFocus: false,
    });

  const { data: aishaInsights, isFetching: aishaInsightsLoading } = useQuery({
    queryKey: ['trend-summary-ai', brandIdFilter, cuisineFilter, cuisineFilter],
    queryFn: async (): Promise<any | undefined> => {
      if (
        !brandIdFilter ||
        !cuisineFilter ||
        !aishaService ||
        !cuisineBenchmark ||
        !restaurants ||
        !popularItems
      ) {
        return null;
      }
      try {
        const payload = {
          delivery_time_cuisine: cuisineBenchmark.delivery_time_current ?? 0,
          rating_brand: brand?.avg_rating_score ?? 0,
          rating_cuisine: cuisineBenchmark.rating_current ?? 0,
          price_average_brand: brand?.avg_price ?? 0,
          price_average_cuisine: cuisineBenchmark.menu_price_current ?? 0,
          new_branches: JSON.stringify(restaurants.new),
          closed_branches: JSON.stringify(restaurants.closed),
          popular_items: popularItems.items
            ? JSON.stringify(popularItems.items)
            : JSON.stringify([]),
          cuisine: cuisineFilter,
          brand: JSON.stringify(brand),
          delivery_time_brand: brand?.avg_delivery_time ?? 0,
        };

        const response: any = await executeFacadeRequest(
          'int-trends',
          aishaService,
          payload,
        );
        return response.result;
      } catch (err) {
        console.error('Error during AI summary generation:', err);
      }
    },
    enabled:
      !!brand &&
      !!cuisineFilter &&
      !!aishaService &&
      !!brand &&
      !popularItemsFetching &&
      !!popularItems &&
      !restaurantsLoading &&
      !!restaurants &&
      !cuisineBenchmarkLoading &&
      !!cuisineBenchmark,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // ---- Renderings
  const RenderInsightFilterText = () => {
    const brandNameText = userBrands?.find(
      (b) => b.id === brandIdFilter,
    )?.brand_name;
    const cuisineText = `${cuisineFilter} cuisine`;
    let areasText = '';
    if (areasFilter?.length) {
      const [first] = areasFilter;
      const extraCount = areasFilter.length - 1;
      areasText = `${first} ${extraCount > 0 ? ` + ${extraCount}` : ''}`;
    }
    const cityText = cityFilter ? `${areasText ? '·' : ''} ${cityFilter}` : '';

    return (
      <Typography className="text-tertiary text-sm font-semibold mb-6">
        Showing insights for {''}
        <span className="text-primary">{brandNameText}</span>
        {' vs '}
        <span className="text-primary">{cuisineText}</span>
        {(areasText || cityText) && ' in'}
        {areasText && (
          <>
            <span className="text-primary"> {areasText} </span>
          </>
        )}
        {cityText && <span className="text-primary">{cityText}</span>}
      </Typography>
    );
  };

  const isLoading =
    !isEmptyState &&
    (popularItemsFetching || cuisineBenchmarkLoading || restaurantsLoading);

  return (
    <>
      <TrendsOnboardingDialog />
      <PageWithTitle title={`Welcome, ${user?.name} 🧠`}>
        <Card className="overflow-hidden">
          <CardHeader className="border-b-tertiary">
            <TrendsFiltersView
              isEmptyState={isEmptyState}
              isLoading={isLoading}
            />
          </CardHeader>

          <CardContent className="bg-quarternary p-4">
            {isEmptyState && !isLoading && (
              <div className="py-8">
                <EmptyStateView />
              </div>
            )}

            {isLoading && <LoadingView />}

            <div hidden={isEmptyState || isLoading} className="animate-fade-in">
              <RenderInsightFilterText />
              <SectionTitle Icon={Target} title="Overview" />
              <div className="grid animate-fade-in grid-cols-12 gap-4">
                <OverviewCardsView
                  avgPrice={avgOfAverages}
                  restaurants={restaurants}
                />
                <div className="col-span-full mt-2">
                  <SectionTitle Icon={ChartLine} title="Trends" />
                </div>
                <AvgPricesView
                  className="col-span-full lg:col-span-6 h-auto"
                  data={avgPricesData}
                  activeFilters={{
                    brandName: brand?.brand_name ?? '',
                    cuisine: cuisineFilter ?? '',
                  }}
                />

                <CuisineBenchmarkView
                  className="col-span-full lg:col-span-6"
                  data={cuisineBenchmark}
                  avgPrice={Number(avgOfAverages.toFixed(2))}
                  isLoading={cuisineBenchmarkLoading}
                  activeFilters={{
                    brandName: brand?.brand_name ?? '',
                    cuisine: cuisineFilter ?? '',
                  }}
                />

                <NewClosedResturantsView
                  className="col-span-full lg:col-span-6"
                  data={restaurants}
                  isLoading={restaurantsLoading || !brandIdFilter}
                />

                <PopularItemsView
                  className="col-span-full lg:col-span-6"
                  isLoading={popularItemsFetching || !brandIdFilter}
                  data={popularItems}
                />

                <AishaInsightsView
                  isLoading={aishaInsightsLoading}
                  className="col-span-full shadow-subtle rounded-2xl mt-2"
                  result={aishaInsights}
                />
              </div>
            </div>
            <PageFooter currentPage="trends" />
          </CardContent>
        </Card>
      </PageWithTitle>
    </>
  );
}
